import PropTypes from 'prop-types'

import { SearchType } from '../graphql/search'
import * as libraryResourceIcons from '../img/library_resource_icons'

export const defaultState = {
  first: 10,
  types: [SearchType.LibraryResource],
  query: null,
  after: null,
  libraryResourceFilters: [
    {
      category: null,
    },
  ],
}

export const resourceTypeUrlMapping = {
  'case-study': 'caseStudy',
  'casi-session': 'casiSession',
  'catalog-training': 'catalogTraining',
  'category-brochure': 'categoryBrochure',
  'journal-article': 'journalArticle',
  'literature-reference': 'literatureReference',
  'patient-brochure': 'patientBrochure',
  'patient-education': 'patientEducation',
  'pharmacy-content': 'pharmacyContent',
  'private-label-form': 'privateLabelForm',
  'product-reference-chart': 'productReferenceChart',
  protocol: 'protocol',
  'social-media-resource': 'socialMediaResource',
  'tech-sheet': 'techSheet',
  'tech-sheet-french': 'techSheetFrench',
  'price-list': 'priceList',
  'white-paper': 'whitePaper',
  webinar: 'webinar',
} as const

export const resourceCategoryUrlMapping = {
  'case-study': 'Case Study',
  'casi-session': 'CASI Session',
  'catalog-training': 'Catalog Training',
  'category-brochure': 'Category Brochure',
  'journal-article': 'Journal Article',
  'literature-reference': 'Literature Reference',
  'patient-brochure': 'Patient Brochure',
  'patient-education': 'Patient Education',
  'pharmacy-content': 'Pharmacy Content',
  'private-label-form': 'Private Label Form',
  'product-reference-chart': 'Product Reference Chart',
  protocol: 'Protocol',
  'social-media-resource': 'Social Media Resource',
  'tech-sheet': 'Tech Sheet',
  'tech-sheet-french': 'Tech Sheet French',
  'price-list': 'Price List',
  'white-paper': 'White Paper',
  webinar: 'Webinar',
} as const

export const resourceTypeSanityMapping = {
  SanityCaseStudy: 'case-study',
  SanityCasiSession: 'casi-session',
  SanityCatalogTraining: 'catalog-training',
  SanityCategoryBrochure: 'category-brochure',
  SanityJournalArticle: 'journal-article',
  SanityLiteratureReference: 'literature-reference',
  SanityPatientBrochure: 'patient-brochure',
  SanityPatientEducation: 'patient-education',
  SanityPharmacyContent: 'pharmacy-content',
  SanityPrivateLabelForm: 'private-label-form',
  SanityProductReferenceChart: 'product-reference-chart',
  SanityProtocol: 'protocol',
  SanitySocialMediaResource: 'social-media-resource',
  SanityTechSheet: 'tech-sheet',
  SanityTechSheetFrench: 'tech-sheet-french',
  SanityPriceList: 'price-list',
  SanityWhitePaper: 'white-paper',
  SanityWebinar: 'webinar',
} as const

export const libraryPropTypes = {
  searchOptions: PropTypes.shape({
    pagination: PropTypes.shape({
      page: PropTypes.number.isRequired,
      size: PropTypes.number.isRequired,
    }).isRequired,
    sort: PropTypes.shape({
      field: PropTypes.string.isRequired,
      order: PropTypes.string.isRequired,
    }).isRequired,
    filters: PropTypes.shape({
      type: PropTypes.arrayOf(PropTypes.string).isRequired,
      name: PropTypes.arrayOf(PropTypes.string).isRequired,
      query: PropTypes.string,
    }).isRequired,
  }),
  meta: PropTypes.shape({
    count: PropTypes.shape({
      all: PropTypes.number.isRequired,
      caseStudy: PropTypes.number,
      casiSession: PropTypes.number,
      catalogTraining: PropTypes.number,
      categoryBrochure: PropTypes.number,
      journalArticle: PropTypes.number,
      literatureReference: PropTypes.number,
      patientBrochure: PropTypes.number,
      patientEducation: PropTypes.number,
      pharmacyContent: PropTypes.number,
      productReferenceChart: PropTypes.number,
      protocol: PropTypes.number,
      socialMediaResource: PropTypes.number,
      techSheet: PropTypes.number,
      techSheetFrench: PropTypes.number,
      priceList: PropTypes.number,
      privateLabelForm: PropTypes.number,
      whitePaper: PropTypes.number,
      webinar: PropTypes.number,
    }),
    pagination: PropTypes.shape({
      totalPages: PropTypes.number.isRequired,
      currentPage: PropTypes.number.isRequired,
      currentCount: PropTypes.number.isRequired,
    }),
  }),
}

// -------- LIBRARY RESOURCE ICONS ----------

type Device = 'desktop' | 'mobile' | 'tablet'

type ResourceType = keyof typeof thumbnails

type ThumbnailEntry =
  | { _tag: 'responsive'; desktop: string; mobile: string; tablet: string }
  | { _tag: 'fixed'; svg: string }

const thumbnails = {
  // new PNG thumbnails provided
  caseStudy: {
    _tag: 'responsive',
    desktop: libraryResourceIcons.caseStudyDesktop,
    mobile: libraryResourceIcons.caseStudyMobile,
    tablet: libraryResourceIcons.caseStudyTablet,
  },
  categoryBrochure: {
    _tag: 'responsive',
    desktop: libraryResourceIcons.categoryBrochureDesktop,
    mobile: libraryResourceIcons.categoryBrochureMobile,
    tablet: libraryResourceIcons.categoryBrochureTablet,
  },
  journalArticle: {
    _tag: 'responsive',
    desktop: libraryResourceIcons.journalArticleDesktop,
    mobile: libraryResourceIcons.journalArticleMobile,
    tablet: libraryResourceIcons.journalArticleTablet,
  },
  literatureReference: {
    _tag: 'responsive',
    desktop: libraryResourceIcons.literatureReferenceDesktop,
    mobile: libraryResourceIcons.literatureReferenceMobile,
    tablet: libraryResourceIcons.literatureReferenceTablet,
  },
  patientBrochure: {
    _tag: 'responsive',
    desktop: libraryResourceIcons.patientBrochureDesktop,
    mobile: libraryResourceIcons.patientBrochureMobile,
    tablet: libraryResourceIcons.patientBrochureTablet,
  },
  patientEducation: {
    _tag: 'responsive',
    desktop: libraryResourceIcons.patientEducationDesktop,
    mobile: libraryResourceIcons.patientEducationMobile,
    tablet: libraryResourceIcons.patientEducationTablet,
  },
  productReferenceChart: {
    _tag: 'responsive',
    desktop: libraryResourceIcons.productReferenceChartDesktop,
    mobile: libraryResourceIcons.productReferenceChartMobile,
    tablet: libraryResourceIcons.productReferenceChartTablet,
  },
  protocol: {
    _tag: 'responsive',
    desktop: libraryResourceIcons.protocolDesktop,
    mobile: libraryResourceIcons.protocolMobile,
    tablet: libraryResourceIcons.protocolTablet,
  },
  techSheet: {
    _tag: 'responsive',
    desktop: libraryResourceIcons.techSheetDesktop,
    mobile: libraryResourceIcons.techSheetMobile,
    tablet: libraryResourceIcons.techSheetTablet,
  },
  webinar: {
    _tag: 'responsive',
    desktop: libraryResourceIcons.webinarDesktop,
    mobile: libraryResourceIcons.webinarMobile,
    tablet: libraryResourceIcons.webinarTablet,
  },
  whitePaper: {
    _tag: 'responsive',
    desktop: libraryResourceIcons.whitePaperDesktop,
    mobile: libraryResourceIcons.whitePaperMobile,
    tablet: libraryResourceIcons.whitePaperTablet,
  },

  // No PNG provided; keep old SVG as fallback
  casiSession: { _tag: 'fixed', svg: libraryResourceIcons.casiSession },
  catalogTraining: { _tag: 'fixed', svg: libraryResourceIcons.catalogTraining },
  pharmacyContent: { _tag: 'fixed', svg: libraryResourceIcons.pharmacyContent },
  priceList: { _tag: 'fixed', svg: libraryResourceIcons.priceList },
  privateLabelForm: { _tag: 'fixed', svg: libraryResourceIcons.privateLabelForm },
  socialMediaResource: { _tag: 'fixed', svg: libraryResourceIcons.socialMediaResource },
  techSheetFrench: { _tag: 'fixed', svg: libraryResourceIcons.techSheetFrench },
} satisfies Record<string, ThumbnailEntry>

export const libraryResourceIcon = ({
  resourceType,
  device = 'desktop',
}: {
  resourceType: ResourceType
  device?: Device
}): string | null => {
  const entry = thumbnails[resourceType]
  if (!entry) {
    return null
  }
  return entry._tag === 'responsive' ? entry[device ?? 'desktop'] : entry.svg
}

export const libraryResourceName = ({ resourceType }: { resourceType: string }) => {
  switch (resourceType) {
    case 'caseStudy':
      return 'Case Study Icon'
    case 'casiSession':
      return 'CASI Session Icon'
    case 'catalogTraining':
      return 'Catalog Training Icon'
    case 'categoryBrochure':
      return 'Category Brochure Icon'
    case 'journalArticle':
      return 'Journal Article Icon'
    case 'literatureReference':
      return 'Literature Reference Icon'
    case 'patientBrochure':
      return 'Patient Brochure Icon'
    case 'patientEducation':
      return 'Patient Education Icon'
    case 'pharmacyContent':
      return 'Pharmacy Content Icon'
    case 'priceList':
      return 'Price List Icon'
    case 'privateLabelForm':
      return 'Private Label Form Icon'
    case 'productReferenceChart':
      return 'Product Reference Chart Icon'
    case 'protocol':
      return 'Protocol Icon'
    case 'socialMediaResource':
      return 'Social Media Resource Icon'
    case 'techSheet':
      return 'Tech Sheet Icon'
    case 'techSheetFrench':
      return 'Tech Sheet French Icon'
    case 'webinar':
      return 'Webinar Icon'
    case 'whitePaper':
      return 'White Paper Icon'
    default:
      return null
  }
}
